import {
  APP_DATA,
  QUERY_PARAM_AUTH_TOKEN,
  QUERY_PARAM_NAME,
  QUERY_PARAM_PREVIEW_AS_ROLE,
  QUERY_PARAM_SKIP_PREVIEW,
  QUERY_PARAM_SKIP_PREVIEW_HEADFUL,
  UI_SETTINGS,
} from "../common/constants";
import { Box, Flex, Loading, Text, styled } from "@100mslive/react-ui";
import React, { useCallback, useContext, useEffect, useState } from "react";
import { isJSON, isStreamingKit } from "../common/utils";
import {
  selectIsLocalAudioPluginPresent,
  selectLocalPeer,
  useHMSActions,
  useHMSStore,
} from "@100mslive/react-sdk";
import {
  useSetAppDataByKey,
  useSetUiSettings,
  useTokenEndpoint,
} from "./AppData/useUISettings";

import { ErrorDialog } from "../primitives/DialogContent";
import { HMSNoiseSuppressionPlugin } from "@100mslive/hms-noise-suppression";
import { Header } from "./Header";
import { LiveHodoContext } from "../contexts/LiveHodoContext";
import PreviewContainer from "./Preview/PreviewContainer";
import { ROLES } from "../common/roles";
import SidePane from "../layouts/SidePane";
import getToken from "../services/tokenService";
import { hmsStore } from "../hms";
import requestService from "../services/requestService";
import { useGetUserData } from "../hooks/useGetUserData";
import { useNavigation } from "./hooks/useNavigation";
import { useNoiseSuppression } from "../plugins/useNoiseSuppression";
import { useParams } from "react-router-dom";
import { useSearchParam } from "react-use";
import { useTranslation } from "react-i18next";
import { v4 as uuid } from "uuid";

/**
 * query params exposed -
 * skip_preview=true => used by recording and streaming service, skips preview and directly joins
 *                      header and footer don't show up in this case
 * skip_preview_headful=true => used by automation testing to skip preview without impacting the UI
 * name=abc => gives the initial name for the peer joining
 * auth_token=123 => uses the passed in token to join instead of fetching from token endpoint
 * ui_mode=activespeaker => lands in active speaker mode after joining the room
 */

const env = process.env.REACT_APP_ENV;
const PreviewScreen = React.memo(({ authTokenByRoomCodeEndpoint }) => {
  const {
    stateCtx: { isJoinCabin },
    setSelectedCabinData,
    setCircleRoomDetail,
    getConferenceSchedules,
  } = useContext(LiveHodoContext);
  const { t } = useTranslation();

  const navigate = useNavigation();
  const hmsActions = useHMSActions();
  const tokenEndpoint = useTokenEndpoint();
  const [, setIsHeadless] = useSetUiSettings(UI_SETTINGS.isHeadless);
  const { roomId: urlRoomId, role: userRole } = useParams(); // from the url
  const userData = useGetUserData();
  const [token, setToken] = useState(null);
  const [error, setError] = useState({ title: "", body: "" });
  // way to skip preview for automated tests, beam recording and streaming
  const beamInToken = useSearchParam("token") === "beam_recording"; // old format to remove
  let skipPreview = useSearchParam(QUERY_PARAM_SKIP_PREVIEW) === "true";
  // use this field to join directly for quick testing while in local
  const directJoinHeadfulFromEnv =
    process.env.REACT_APP_HEADLESS_JOIN === "true";
  const directJoinHeadful =
    useSearchParam(QUERY_PARAM_SKIP_PREVIEW_HEADFUL) === "true" ||
    directJoinHeadfulFromEnv;
  skipPreview = skipPreview || beamInToken || directJoinHeadful;
  const [initialName, setInitialName] = useState("");
  const nameRaw =
    useSearchParam(QUERY_PARAM_NAME) || (skipPreview ? "Beam" : "");
  const previewAsRole = useSearchParam(QUERY_PARAM_PREVIEW_AS_ROLE);
  let authToken = useSearchParam(QUERY_PARAM_AUTH_TOKEN);

  const [isNoiseSuppressedState, , handleNoiseSuppression] =
    useNoiseSuppression();

  useEffect(() => {
    if (!isNoiseSuppressedState) return;
    handleNoiseSuppression();
  }, [handleNoiseSuppression, isNoiseSuppressedState]);

  const checkMyRole = useCallback(async () => {
    if (!userData?.id) return;

    try {
      const circle_id = Number(localStorage.getItem("circle_id"));
      const user_id = userData?.id;
      const res = await requestService
        .checkMyRoleConferenceRoom({
          circle_id,
          user_id,
        })
        .then(data => data.json());

      const keyRole = isStreamingKit() ? "livestream" : "conference";
      const roles =
        typeof res?.one_hundred_ms_role === "string" &&
        isJSON(res?.one_hundred_ms_role)
          ? JSON.parse(res?.one_hundred_ms_role)
          : {
              [keyRole]: res?.one_hundred_ms_role,
            };
      const myRole = isStreamingKit()
        ? roles[keyRole] || ROLES.HLS_VIEWER
        : roles[keyRole] || ROLES.GUEST;

      if (myRole === userRole) {
        return true;
      }

      return false;
    } catch (error) {
      console.log(error);
    }
  }, [userData]);

  useEffect(() => {
    setSelectedCabinData(null);
  }, []);

  const getRoomStreamDetail = useCallback(async () => {
    try {
      if (!urlRoomId) return;

      const res = await requestService
        .getRoomStreamByRoomId(urlRoomId)
        .then(data => data.json());
      setCircleRoomDetail(res);
    } catch (error) {
      console.log(error);
    }
  }, [urlRoomId]);

  const getRoomConferenceDetail = useCallback(async () => {
    try {
      if (!urlRoomId) return;

      const res = await requestService
        .getRoomConferenceByRoomId(urlRoomId)
        .then(data => data.json());
      setCircleRoomDetail(res);
    } catch (error) {
      console.log(error);
    }
  }, [urlRoomId]);

  useEffect(() => {
    (async () => {
      try {
        if (!urlRoomId) return;
        const params = {
          circle_id: Number(localStorage.getItem("circle_id")),
          sort_by: "schedule_date",
          order: "asc",
          page_size: 1000,
          page_num: 1,
        };
        await getConferenceSchedules(params);
      } catch (error) {
        console.log(error);
      }
    })();
  }, [urlRoomId]);

  useEffect(() => {
    (async () => {
      if (authToken) {
        setToken(authToken);
        return;
      }
      if (!tokenEndpoint || !urlRoomId) {
        return;
      }

      const roomCode = !userRole && urlRoomId;

      // if (userRole !== ROLES.HLS_VIEWER) {
      if (userRole) {
        if (!userData?.id) return;

        const isJoined = await checkMyRole();

        if (!isJoined && !isStreamingKit()) {
          setError(
            convertPreviewError({
              action: "CHECK_ROLE",
              title: t("lbl_error_check_role"),
              body: t("msg_error_check_role"),
            })
          );
          return;
        }

        const getTokenFn = roomCode
          ? () =>
              hmsActions.getAuthTokenByRoomCode(
                { roomCode },
                { endpoint: authTokenByRoomCodeEndpoint }
              )
          : () =>
              getToken(
                tokenEndpoint,
                userData?.id || uuid(),
                userRole,
                urlRoomId
              );

        getTokenFn()
          .then(token => {
            setToken(token);
          })
          .catch(error => {
            setError(convertPreviewError(error));
          });

        isStreamingKit() ? getRoomStreamDetail() : getRoomConferenceDetail();
      } else {
        const getTokenFn = roomCode
          ? () =>
              hmsActions.getAuthTokenByRoomCode(
                { roomCode },
                { endpoint: authTokenByRoomCodeEndpoint }
              )
          : () =>
              getToken(
                `https://prod-in2.100ms.live/hmsapi/man-videoconf-1111.app.100ms.live/api/token`,
                uuid(),
                userRole,
                urlRoomId
              );

        getTokenFn()
          .then(token => {
            setToken(token);
          })
          .catch(error => {
            setError(convertPreviewError(error));
          });
      }

      // const name =
      //   `${userData?.doctor?.chuc_vu ? `${userData?.doctor?.chuc_vu} - ` : ""}${
      //     userData?.name
      //   }` || nameRaw;
      const name =
        userRole === ROLES.E_V || userRole === ROLES.F_V
          ? userRole.replace("-", "").toUpperCase()
          : `${userData?.name}` || nameRaw;
      setInitialName(name);
    })();
  }, [
    hmsActions,
    tokenEndpoint,
    urlRoomId,
    userRole,
    userData,
    authToken,
    authTokenByRoomCodeEndpoint,
  ]);

  const onJoin = () => {
    !directJoinHeadful && setIsHeadless(skipPreview);
    let meetingURL = isJoinCabin
      ? userRole === ROLES.GUEST
        ? `/select/${urlRoomId}`
        : `/meeting/${urlRoomId}`
      : `/meeting/${urlRoomId}`;
    if (userRole) {
      meetingURL += `/${userRole}`;
    }
    navigate(meetingURL);
  };

  if (error.title) {
    return <ErrorDialog title={error.title}>{error.body}</ErrorDialog>;
  }
  return (
    <Flex
      direction="column"
      css={{
        size: "100%",
        position: "relative",
      }}
    >
      <Box
        css={{
          h: "$18",
          "@md": { h: "$32", flexShrink: 0 },
        }}
        data-testid="header"
      >
        <Header isPreview={true} />
      </Box>
      <Flex
        css={{
          flex: "1 1 0",
          position: "relative",
          overflowY: "auto",
          alignItems: "center",
          "@xl": {
            alignItems: "start",
          },
        }}
        justify="center"
      >
        {token ? (
          <>
            <PreviewContainer
              initialName={initialName}
              skipPreview={skipPreview}
              env={env}
              onJoin={onJoin}
              token={token}
              asRole={previewAsRole}
            />
          </>
        ) : token !== null ? (
          <Loading size={100} />
        ) : (
          <Text
            css={{ c: "$textMedEmp", my: "$6", textAlign: "center" }}
            variant="body1"
          >
            {t("fm_not_connect_room", {
              name: t(isStreamingKit() ? "stream" : "lbl_meting"),
            })}
          </Text>
        )}
        <SidePane
          css={{
            position: "unset",
            mr: "$10",
            "@lg": { position: "fixed", mr: "$0" },
          }}
        />
      </Flex>
    </Flex>
  );
});

const convertPreviewError = error => {
  console.error("[error]", { error });
  if (error.action === "GET_TOKEN" && error.code === 403) {
    return {
      title: t("lbl_room_code_is_disabled"),
      body: ErrorWithSupportLink(t("msg_room_code_is_disabled")),
    };
  } else if (error.action === "GET_TOKEN" && error.code === 404) {
    return {
      title: t("lbl_room_code_does_not_exist"),
      body: ErrorWithSupportLink(t("msg_room_code_does_not_exist")),
    };
  } else if (error.action === "GET_TOKEN" && error.code === 2003) {
    return {
      title: t("lbl_endpoint_is_not_reachable"),
      body: ErrorWithSupportLink(
        t("fm_endpoint_is_not_reachable", { desc: error.description })
      ),
    };
  } else if (error.response && error.response.status === 404) {
    return {
      title: t("lbl_room_does_not_exist"),
      body: ErrorWithSupportLink(t("msg_room_does_not_exist")),
    };
  } else if (error.response && error.response.status === 403) {
    return {
      title: t("lbl_accessing_disabled"),
      body: ErrorWithSupportLink(t("msg_accessing_disabled")),
    };
  } else if (error.action === "CHECK_ROLE") {
    return {
      title: error.title,
      body: ErrorWithSupportLink(error.body),
    };
  } else {
    console.error("Token API Error", error);
    return {
      title: t("lbl_error_fetching_token"),
      body: ErrorWithSupportLink(t("msg_error_fetching_token")),
    };
  }
};

// const Link = styled("a", {
//   color: "#2f80e1",
// });

export const ErrorWithSupportLink = errorMessage => (
  <div>
    {errorMessage}
    {/* If you think this is a mistake on our side, please create{" "}
    <Link
      target="_blank"
      href="https://github.com/100mslive/100ms-web/issues"
      rel="noreferrer"
    >
      an issue
    </Link>{" "}
    or reach out over{" "}
    <Link
      target="_blank"
      href="https://discord.com/invite/kGdmszyzq2"
      rel="noreferrer"
    >
      Discord
    </Link>
    . */}
  </div>
);

export default PreviewScreen;
